import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { X } from 'lucide-react';
import { HexColorInput, HexColorPicker } from 'react-colorful';

import { apiPatch } from '../../lib/api_v2';
import db from '../../lib/db';
import { logError } from '../../lib/logger';
import { normalizeProfileTagColor } from '../../lib/profileTag';
import { cn } from '../../lib/utils';
import { responseValidations } from '../settings/account/accountStyles';
import { SETTINGS_MODAL_SAVE_PILL_CLASS } from './settingsModalChrome';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';

import ProfileTagPreview from './ProfileTagPreview';
import { Tooltip } from './Tooltip';

const FIELD_LABEL_CLASS = 'block text-cursed text-black dark:text-neutral-100';
const NAME_TAG_INPUT_CLASS = cn(
  'h-12 w-full rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 text-[15px] leading-5 text-black placeholder:text-gray-400',
  'hover:bg-white focus:outline-none',
  'dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500',
);
const NAME_TAG_INPUT_WITH_DELETE_PADDING = 'pr-12';

// scopeNoun/scopeTitle let the DM preferences modal reword the preview scope
// ("this conversation: Noah Wilson") without forking the panel.
const GroupProfilePanel = ({ onSaved, scopeNoun = 'group', scopeTitle }) => {
  const { profile, profileId, workspace, workspaceId } = useRouteWorkspace();
  const initialTag = profile?.tag || '';
  const initialTagColor = normalizeProfileTagColor(profile?.tag_color);
  const [tag, setTag] = useState(initialTag);
  const [tagColor, setTagColor] = useState(initialTagColor);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const trimmedTag = useMemo(() => tag.trim(), [tag]);
  const tagColorSwatchStyle = useMemo(() => ({ backgroundColor: tagColor }), [tagColor]);
  const tagError = Array.isArray(errors.tag) ? errors.tag.join(', ') : errors.tag;
  const tagColorError = Array.isArray(errors.tag_color) ? errors.tag_color.join(', ') : errors.tag_color;
  const hasChanges = tag !== initialTag || tagColor !== initialTagColor;
  const isDisabled = isSaving || !workspaceId || !profileId || !hasChanges;
  const previewScopeTitle = scopeTitle || workspace?.title || `this ${scopeNoun}`;

  useEffect(() => {
    setTag(initialTag);
    setTagColor(initialTagColor);
    setErrors({});
  }, [initialTag, initialTagColor, profileId]);

  const handleTagChange = useCallback((event) => {
    setTag(event.target.value);
    setErrors((prev) => ({ ...prev, tag: null, general: null }));
  }, []);

  const handleTagColorChange = useCallback((nextColor) => {
    setTagColor(normalizeProfileTagColor(nextColor));
    setErrors((prev) => ({ ...prev, tag_color: null, general: null }));
  }, []);

  const saveTag = useCallback(
    async (nextTag, nextTagColor) => {
      if (!workspaceId || !profileId) return;

      setIsSaving(true);
      setErrors({});

      try {
        const response = await apiPatch(`/profiles/${profileId}`, {
          profile: {
            tag: nextTag,
            tag_color: nextTagColor,
          },
        });
        const data = await response.json();
        const updatedProfile = data?.data?.profile;

        if (!response.ok || data.ok !== true || !updatedProfile) {
          const validationErrors = responseValidations(data);
          setErrors(validationErrors || { general: 'Unable to save name tag.' });
          return;
        }

        await db.profiles.put(updatedProfile);
        setTag(updatedProfile.tag || '');
        setTagColor(normalizeProfileTagColor(updatedProfile.tag_color));
        // Popping back to the settings root doubles as the success feedback
        onSaved?.();
      } catch (saveError) {
        logError('GroupProfilePanel', 'Failed to save name tag', { error: saveError });
        setErrors({ general: 'Unable to save name tag.' });
      } finally {
        setIsSaving(false);
      }
    },
    [onSaved, profileId, workspaceId],
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (isDisabled) return;
      saveTag(trimmedTag, tagColor);
    },
    [isDisabled, saveTag, tagColor, trimmedTag],
  );

  // Delete only clears the field locally — the no-tag state commits through Save like any other change
  const handleDeleteTag = useCallback(() => {
    if (isSaving) return;
    setTag('');
    setErrors((prev) => ({ ...prev, tag: null, general: null }));
  }, [isSaving]);

  if (!profile) return null;

  return (
    <form onSubmit={handleSubmit} className="w-full pt-10">
      {/* Sits in the modal top bar, opposite the back button */}
      <button
        type="submit"
        data-app-region="none"
        disabled={isDisabled}
        className={SETTINGS_MODAL_SAVE_PILL_CLASS}
      >
        {isSaving ? '...' : 'Save'}
      </button>

      {errors.general && (
        <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{errors.general}</div>
      )}

      <label htmlFor="profile_tag" className={FIELD_LABEL_CLASS}>
        Name Tag
      </label>
      <div className="relative mt-2">
        <input
          id="profile_tag"
          name="tag"
          type="text"
          maxLength={20}
          value={tag}
          onChange={handleTagChange}
          placeholder="None"
          autoComplete="off"
          data-1p-ignore="true"
          className={cn(NAME_TAG_INPUT_CLASS, tag.length > 0 && NAME_TAG_INPUT_WITH_DELETE_PADDING)}
        />
        {tag.length > 0 && (
          <Tooltip label="Delete" placement="top" className="absolute top-1/2 right-4 flex -translate-y-1/2">
            <button
              type="button"
              data-app-region="none"
              onClick={handleDeleteTag}
              disabled={isSaving}
              aria-label="Delete name tag"
              className="flex size-5 cursor-pointer items-center justify-center rounded-full bg-black text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-300"
            >
              <X size={10} strokeWidth={1.5} absoluteStrokeWidth />
            </button>
          </Tooltip>
        )}
      </div>
      {tagError && <span className="mt-1 block text-left text-xs text-red-400">{tagError}</span>}

      <div className="mt-5">
        <span className={FIELD_LABEL_CLASS}>Color</span>
        <div className="relative mt-2">
          <span
            className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 rounded-full border border-black/10 dark:border-white/10"
            style={tagColorSwatchStyle}
          />
          <HexColorInput
            color={tagColor}
            onChange={handleTagColorChange}
            prefixed
            aria-label="Tag color hex"
            className={cn(
              'h-12 w-full rounded-lg border sm:border-[0.5px] bg-gray-50 pr-4 pl-11 text-[15px] leading-5 text-black hover:bg-white focus:outline-none dark:bg-neutral-900 dark:text-neutral-100',
              tagColorError ? 'border-red-500' : 'border-white dark:border-neutral-800',
            )}
          />
        </div>
        <HexColorPicker color={tagColor} onChange={handleTagColorChange} className="mt-3 !h-30 !w-full" aria-label="Tag color picker" />
        {tagColorError && <span className="mt-1 block text-left text-xs text-red-400">{tagColorError}</span>}
      </div>

      <div className="mt-5 flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-white bg-gray-100 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:border-white/10 dark:bg-neutral-900">
        <div className="flex flex-col gap-1 p-4">
          <span className="text-[15px] leading-5 font-medium text-black dark:text-neutral-100">Preview</span>
          <p className="text-cursed leading-[18px] text-gray-500 dark:text-neutral-400">
            This tag will only appear for this {scopeNoun}: <span className="text-black dark:text-neutral-100">{previewScopeTitle}</span>
          </p>
        </div>
        <div className="flex min-h-24 w-full items-center border-t border-gray-200 bg-gray-50 px-4 py-4 dark:border-neutral-800 dark:bg-neutral-950">
          <ProfileTagPreview tag={trimmedTag} tagColor={tagColor} />
        </div>
      </div>
    </form>
  );
};

export default GroupProfilePanel;
